/**
 * Virtual File System
 *
 * IndexedDB-backed document storage used as the persistent workspace when
 * the File System Access API is unavailable (Firefox / Safari). It models a
 * simple folder hierarchy so the custom file browser dialog can navigate,
 * open, save, organize and delete documents entirely in-app.
 */

const DB_NAME = 'yap-virtual-fs';
const DB_VERSION = 1;
const STORE = 'entries';
export const ROOT_FOLDER_ID = 'root';

export interface VfsEntry {
    id: string;
    parentId: string;
    name: string;
    type: 'folder' | 'file';
    /** File semantic — mirrors the document kind ('project' | 'template'). */
    kind?: 'project' | 'template';
    content?: string;
    createdAt: string;
    modifiedAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, { keyPath: 'id' });
                store.createIndex('parentId', 'parentId', { unique: false });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function withStore<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T> {
    return openDatabase().then(
        (db) =>
            new Promise<T>((resolve, reject) => {
                const tx = db.transaction(STORE, mode);
                const store = tx.objectStore(STORE);
                const request = run(store);
                tx.oncomplete = () => {
                    db.close();
                    resolve(request ? request.result : (undefined as T));
                };
                tx.onerror = () => reject(tx.error);
            })
    );
}

function newId(): string {
    return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

/** Lists every entry (folders + files) that lives directly under a folder. */
export async function listChildren(parentId: string): Promise<VfsEntry[]> {
    const children = await withStore<VfsEntry[]>('readonly', (store) =>
        store.index('parentId').getAll(parentId) as IDBRequest<VfsEntry[]>
    );
    // Folders first, then files; each group alphabetical.
    return children.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
}

export async function getEntry(id: string): Promise<VfsEntry | undefined> {
    return withStore<VfsEntry | undefined>('readonly', (store) => store.get(id));
}

export async function createFolder(parentId: string, name: string): Promise<VfsEntry> {
    const now = new Date().toISOString();
    const entry: VfsEntry = { id: newId(), parentId, name, type: 'folder', createdAt: now, modifiedAt: now };
    await withStore('readwrite', (store) => { store.put(entry); });
    return entry;
}

/**
 * Creates or replaces a file inside a folder (upsert by name, like saving
 * over an existing document). Returns the stored entry id.
 */
export async function writeFile(
    parentId: string,
    name: string,
    content: string,
    kind: 'project' | 'template'
): Promise<string> {
    const siblings = await listChildren(parentId);
    const existing = siblings.find((e) => e.type === 'file' && e.name === name);
    const now = new Date().toISOString();
    const entry: VfsEntry = existing
        ? { ...existing, content, kind, modifiedAt: now }
        : { id: newId(), parentId, name, type: 'file', kind, content, createdAt: now, modifiedAt: now };
    await withStore('readwrite', (store) => { store.put(entry); });
    return entry.id;
}

export async function deleteEntry(id: string): Promise<void> {
    const entry = await getEntry(id);
    if (!entry) return;
    if (entry.type === 'folder') {
        const children = await listChildren(id);
        for (const child of children) {
            await deleteEntry(child.id);
        }
    }
    await withStore('readwrite', (store) => { store.delete(id); });
}

export async function renameEntry(id: string, name: string): Promise<void> {
    const entry = await getEntry(id);
    if (!entry) return;
    await withStore('readwrite', (store) => {
        store.put({ ...entry, name, modifiedAt: new Date().toISOString() });
    });
}

/**
 * Resolves the breadcrumb chain (root → … → folder) for the given entry,
 * used by the file browser's navigation bar.
 */
export async function getFolderPath(folderId: string): Promise<VfsEntry[]> {
    const chain: VfsEntry[] = [];
    let cursorId: string | null = folderId === ROOT_FOLDER_ID ? null : folderId;
    while (cursorId) {
        const entry = await getEntry(cursorId);
        if (!entry) break;
        chain.unshift(entry);
        cursorId = entry.parentId === ROOT_FOLDER_ID ? null : entry.parentId;
    }
    return chain;
}
